import {
	action,
	computed,
	observable,
	runInAction,
	reaction,
	IReactionDisposer,
} from "mobx";
import { LoaderStore } from "../common";
import {
	CountryDataType,
	EstimatedGamingAmountDataType,
	RegistrationFormData,
	GamingLimitDataType,
	LanguageDataType,
	LimitDurationDataType,
	OccupationDataType,
	OccupationGroupDataType,
	PasswordSimilarityRequestModel,
	RegionDataType,
	CreateUserDto,
	RegistrationLimitOption,
} from "@data-types/membership/registration";
import { DateTime } from "luxon";
import { getAgencyKey } from "@utils";
import { RegistrationLookupService } from "@services/membership/registration";
import { LookupItem } from "@data-types";
import { localizationService } from "@state/services";
import { validateBooleanResponse } from "@utils/lookups/validators";
import {
	PlatformAuthAvailabilityApiService,
	PlatformGamingLimitDurationsApiService,
	PlatformPasswordAvailabilityApiService,
} from "@api-services/platform";
import { getRedirectOriginUrl } from "@utils/specific/capacitor/helpers";
import { analyticsTracker } from "@utils/analytics";
import { RegistrationInteractionTypeEnum } from "@data-types/analytics-tracker/RegistrationInteractionTypeEnum";
import { logger } from "@utils/logger/logger";
import { getRegistrationCampaign } from "@utils/analytics/registrationCampaignStorage";

export class MainRegistrationStore {
	@observable titleData: LookupItem[] | null;
	@observable countryData: CountryDataType[] | null;
	@observable languageData: LanguageDataType[] | null;
	@observable countryRegionsData: RegionDataType[] | null;
	@observable gamingLimitsData: GamingLimitDataType | null;
	@observable limitDurationData: LimitDurationDataType[] | null;
	@observable estimatedGamingAmountData:
		EstimatedGamingAmountDataType[] | null;
	@observable occupationData: OccupationDataType[] | null;
	@observable occupationGroupData: OccupationGroupDataType[] | null;

	@observable currentStep = 1;
	@observable totalSteps = 2;
	@observable isRegistrationSuccessful: boolean | null = null;
	@observable isFirstStepDone: boolean = false;
	@observable isSwitchingSteps: boolean = false;

	loader: LoaderStore;
	lookupService: RegistrationLookupService;

	protected isRegistrationInteractionTrackingEnabled = false;

	protected currentStepReactionDisposer: IReactionDisposer;

	constructor() {
		this.loader = new LoaderStore();
		this.lookupService = new RegistrationLookupService();

		this.currentStepReactionDisposer = reaction(
			() => this.currentStep,
			(step) => {
				analyticsTracker.trackRegistrationProcess(step);

				if (this.isRegistrationInteractionTrackingEnabled) {
					this.trackStepInteraction(step);
				}
			}
		);
	}

	private trackStepInteraction(step: number) {
		if (step === 2) {
			analyticsTracker.trackRegistrationInteraction(
				RegistrationInteractionTypeEnum.StepTwoReached
			);

			return;
		}

		if (step === 1) {
			analyticsTracker.trackRegistrationInteraction(
				RegistrationInteractionTypeEnum.StepOneReturned
			);
		}
	}

	@computed get isLoading() {
		return this.loader.isLoading;
	}

	@computed get isLoadingProcess() {
		return this.loader.isLoadingProcess;
	}

	@action.bound
	setCurrentStep(step: number, isValid?: boolean) {
		if (isValid || this.currentStep > step) {
			if (step === 2 && !this.isFirstStepDone) {
				this.isFirstStepDone = true;
			}
			this.currentStep = step;
			document.querySelector("main")?.scrollTo(0, 0);
		}
	}

	@action.bound
	previousStep() {
		if (this.currentStep > 1) {
			this.currentStep--;
			document.querySelector("main")?.scrollTo(0, 0);
		}
	}

	@action.bound
	nextStep() {
		if (this.currentStep < this.totalSteps) {
			this.currentStep++;
			document.querySelector("main")?.scrollTo(0, 0);
		}
	}

	@action.bound
	toggleStepSwitchFlag(value: boolean) {
		if (this.isSwitchingSteps != value) {
			this.isSwitchingSteps = value;
		}
	}

	@action.bound
	async checkUserNameAvailability(username: string) {
		try {
			const response =
				await PlatformAuthAvailabilityApiService.checkUserNameAvailability(
					username
				);
			return validateBooleanResponse(
				response,
				"mainRegistrationStore-checkUserNameAvailability"
			);
		} catch (error) {
			console.error(error);
			return false;
		}
	}

	@action.bound
	async checkIsEmailAvailable(email: string) {
		try {
			const response =
				await PlatformAuthAvailabilityApiService.checkEmailAvailability(
					email
				);
			return validateBooleanResponse(
				response,
				"mainRegistrationStore-checkIsEmailAvailable"
			);
		} catch (error) {
			console.error(error);
			return false;
		}
	}

	@action.bound
	async checkPasswordSimilarity(dataset: PasswordSimilarityRequestModel) {
		try {
			const response =
				await PlatformPasswordAvailabilityApiService.checkPasswordSimilarity(
					dataset
				);
			return validateBooleanResponse(
				response,
				"mainRegistrationStore-checkPasswordSimilarity"
			);
		} catch (error) {
			console.error(error);
			return false;
		}
	}

	@action.bound
	async fetchLimitDuration() {
		try {
			const response =
				await PlatformGamingLimitDurationsApiService.findLimitDurations();

			runInAction(() => {
				this.limitDurationData = response.item.sort(
					(a, b) => a.units - b.units
				);
			});
		} catch (error) {
			logger.logError(error);
		}
	}

	async fetchLookups(errCallback?: () => void) {
		this.loader.suspend();

		try {
			this.titleData = await RegistrationLookupService.getTitles();
			this.countryData = await RegistrationLookupService.getCountries();
			this.languageData = await RegistrationLookupService.getLanguages();
			this.countryRegionsData =
				await RegistrationLookupService.getCountryRegions(
					this.countryData
				);
			this.gamingLimitsData =
				await RegistrationLookupService.getGamingLimits();
			this.occupationData =
				await RegistrationLookupService.getOccupations();
			this.occupationGroupData =
				await RegistrationLookupService.getOccupationGroups();
			this.estimatedGamingAmountData =
				await RegistrationLookupService.getEstimatedGamingAmounts();
		} catch (error) {
			errCallback && errCallback();
			this.isRegistrationSuccessful = false;

			App.state.rootStore.notificationStore.error(
				localizationService.t(
					"MEMBERSHIP.ACCOUNT_ACTIVATION.ERROR_HAS_OCCURED"
				)
			);
		} finally {
			this.loader.resume();
		}
	}

	@action.bound
	prepareRegistrationFormData(
		data: RegistrationFormData,
		affiliateCode?: string | null,
		partnerIdentificator?: string
	): CreateUserDto {
		let createUserData = {
			acceptTermsAndConditions: data.acceptTermsAndConditions,
			activationUrl: getRedirectOriginUrl(
				"auth/register-activation?activationToken={{activationToken}}"
			),
			applicationIdentifier: getAgencyKey(),
			confirmPassword: data.confirmPassword,
			countryId: data.countryId,
			city: data.city,
			email: data.email,
			houseNumber: data.houseNumber,
			password: data.password,
			street: data.street,
			username: data.username,
			zipCode: data.zipCode,
			titleId: data.titleId,
			userTitleId: data.userTitleId,
		} as CreateUserDto;

		if (affiliateCode) {
			createUserData.affiliateCode = affiliateCode;
		}

		if (data.birthName && data.birthName != "") {
			createUserData.birthName = data.birthName.trim();
		}

		if (data.birthPlace && data.birthPlace != "") {
			createUserData.birthPlace = data.birthPlace.trim();
		}

		if (data.city && data.city != "") {
			createUserData.city = data.city.trim();
		}

		if (data.cultureInfoName) {
			createUserData.cultureInfoName = data.cultureInfoName;
		}

		if (data.day && data.month && data.year) {
			createUserData.dob = DateTime.fromObject({
				day: parseInt(data.day),
				month: parseInt(data.month),
				year: parseInt(data.year),
			}).toFormat("yyyy-MM-dd");
		}

		if (data.dob) {
			createUserData.dob = DateTime.fromISO(data.dob.toString()).toFormat(
				"yyyy-MM-dd"
			);
		}

		if (data.estimatedGamingAmountId) {
			createUserData.estimatedGamingAmountId =
				data.estimatedGamingAmountId;
		}

		if (data.firstName && data.firstName != "") {
			createUserData.firstName = data.firstName;
		}

		if (data.lastName && data.lastName != "") {
			createUserData.lastName = data.lastName;
		}

		if (data.limitOption === RegistrationLimitOption.Takeover) {
			createUserData.isLimitTakeover = true;
		} else if (data.limitAmount && data.limitAmount != "") {
			createUserData.limitAmount = data.limitAmount;
		}

		if (data.limitDurationId) {
			createUserData.limitDurationId = data.limitDurationId;
		}

		if (data.languageId) {
			//find language id from culture
			const selectedLanguage = this.languageData?.find(
				(lang) => lang.culture === data.languageId
			);

			if (selectedLanguage) {
				createUserData.languageId = selectedLanguage.id;
			}
		}

		if (data.mobilePhone) {
			createUserData.mobilePhone = data.mobilePhone.includes(" ")
				? data.mobilePhone.replaceAll(" ", "")
				: data.mobilePhone;
		}

		if (data.nationality) {
			createUserData.nationality = data.nationality;
		}

		if (data.nationalityId) {
			createUserData.nationalityId = data.nationalityId;
		}

		if (data.occupationId) {
			createUserData.occupationId = data.occupationId;
		}

		if (data.personalIdentificator) {
			createUserData.personalIdentificator = data.personalIdentificator;
		}

		if (partnerIdentificator) {
			createUserData.partnerIdentificator = partnerIdentificator;
		}

		if (data.playerPromoSendingMode) {
			createUserData.playerPromoSendingMode = JSON.parse(
				data.playerPromoSendingMode
			);
		}
		if (data.regionId) {
			createUserData.regionId = data.regionId;
		}
		return createUserData;
	}

	protected addCampaignAttribution(body: CreateUserDto) {
		const campaign = getRegistrationCampaign();

		if (campaign?.campaignId) {
			body.campaignId = campaign.campaignId;
		}

		if (campaign?.recipientId) {
			body.recipientId = campaign.recipientId;
		}
	}
}
